import React, { useState, useMemo } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const deg2rad = (d) => (d * Math.PI) / 180;

// Arrowhead triangle points for a phasor ending at (tx, ty) with angle theta_rad
const arrowPoints = (tx, ty, rad, len = 11, hw = 4.5) => {
  const bx = tx - len * Math.cos(rad);
  const by = ty + len * Math.sin(rad); // +sin because SVG y is flipped
  const c1x = bx + hw * Math.sin(rad);
  const c1y = by + hw * Math.cos(rad);
  const c2x = bx - hw * Math.sin(rad);
  const c2y = by - hw * Math.cos(rad);
  return `${tx},${ty} ${c1x},${c1y} ${c2x},${c2y}`;
};

const PHASOR_DEFS = [
  { label: 'Va', offsetDeg: 0,    color: '#0d9488', darkColor: '#5eead4', bgColor: '#ccfbf1' },
  { label: 'Vb', offsetDeg: -120, color: '#6366f1', darkColor: '#a5b4fc', bgColor: '#e0e7ff' },
  { label: 'Vc', offsetDeg: 120,  color: '#f59e0b', darkColor: '#fcd34d', bgColor: '#fef3c7' },
];

const CX = 165, CY = 165, DR = 110; // display radius in SVG pixels

const PhasorVisualizer = () => {
  const { dark } = useTheme();
  const [vmag, setVmag] = useState(7.967);
  const [thetaA, setThetaA] = useState(0);

  const phasors = useMemo(() =>
    PHASOR_DEFS.map(({ label, offsetDeg, color, darkColor, bgColor }) => {
      const angle = thetaA + offsetDeg;
      const rad = deg2rad(angle);
      const tx = CX + DR * Math.cos(rad);
      const ty = CY - DR * Math.sin(rad); // flip y
      // Line ends short of tip so arrowhead fits neatly
      const ex = CX + (DR - 12) * Math.cos(rad);
      const ey = CY - (DR - 12) * Math.sin(rad);
      // Normalise display angle to (−180, +180]
      let disp = ((angle % 360) + 360) % 360;
      if (disp > 180) disp -= 360;
      return {
        label, rad, tx, ty, ex, ey, disp,
        color: dark ? darkColor : color,
        bgColor,
        re: vmag * Math.cos(deg2rad(angle)),
        im: vmag * Math.sin(deg2rad(angle)),
      };
    }), [vmag, thetaA, dark]);

  const sum = useMemo(() => ({
    re: phasors.reduce((s, p) => s + p.re, 0),
    im: phasors.reduce((s, p) => s + p.im, 0),
  }), [phasors]);

  const axisStroke = dark ? '#334155' : '#e2e8f0';
  const gridDash  = dark ? '#1e293b' : '#f1f5f9';
  const textFill  = dark ? '#64748b' : '#94a3b8';
  const bgFill    = dark ? '#0f172a' : '#ffffff';

  return (
    <div data-testid="phasor-visualizer" className="space-y-5">
      {/* Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              V<sub>rms</sub> magnitude (kV)
            </label>
            <TeachingTooltip
              showIcon
              whatThis="The RMS (root-mean-square) voltage magnitude of each phase. Power systems always use RMS values — for a 13.8 kV line-to-line system the phase voltage is 7.967 kV RMS."
              tryThis="Drag up from 7.967 to 14.0 kV and watch the table values scale — all three phasors grow equally, keeping 120° spacing."
              whyMatters="Using peak voltage (V_m = √2 × V_rms) in power formulas gives answers that are √2 too large. Equipment nameplates, relay settings, and load flow studies all assume RMS."
              connectedTo="→ Updates the phasor table below (polar and rectangular columns)."
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="vmag-input"
              type="range" min="0.1" max="200" step="0.001" value={vmag}
              onChange={e => setVmag(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <div className="flex items-center gap-1">
              <input
                type="number" value={vmag} step="0.001" min="0.1"
                onChange={e => setVmag(Math.max(0.1, Number(e.target.value)))}
                className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
              />
              <span className="text-xs text-slate-500 w-7">kV</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              θ<sub>a</sub> — V<sub>a</sub> reference angle (°)
            </label>
            <TeachingTooltip
              showIcon
              whatThis="The phase angle of the A-phase voltage phasor, measured counterclockwise from the positive real axis. By convention, Va is set to 0° as the reference phasor in fault analysis."
              tryThis="Rotate θ_a to 90° and watch the entire diagram rotate — all three phasors maintain exactly 120° spacing because the system is balanced."
              whyMatters="In short-circuit analysis, the prefault voltage is 1.0 pu ∠0°, so setting Va as reference simplifies the algebra. Choosing any other angle gives the same magnitudes but more complex phase book-keeping."
              connectedTo="→ Rotates all three phasors; Vb = Va − 120°, Vc = Va + 120° by definition of positive (ABC) sequence."
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="theta-input"
              type="range" min="-180" max="180" step="1" value={thetaA}
              onChange={e => setThetaA(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <div className="flex items-center gap-1">
              <input
                type="number" value={thetaA} min="-180" max="180"
                onChange={e => setThetaA(Math.max(-180, Math.min(180, Number(e.target.value))))}
                className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none"
              />
              <span className="text-xs text-slate-500 w-4">°</span>
            </div>
          </div>
        </div>
      </div>

      {/* Diagram + Table */}
      <div className="flex flex-col sm:flex-row gap-6 items-start">

        {/* SVG Phasor diagram */}
        <div className="w-full sm:w-auto flex-shrink-0">
          <p className="text-xs text-slate-500 dark:text-slate-500 mb-1.5 text-center">
            Complex plane — balanced ABC sequence
          </p>
          <svg
            data-testid="phasor-svg"
            viewBox="0 0 330 330"
            className="w-full max-w-[280px] mx-auto block"
            style={{ minWidth: 220 }}
          >
            <rect width="330" height="330" fill={bgFill} rx="10" />

            {/* Grid circles at 50% and 100% */}
            {[0.5, 1.0].map(f => (
              <circle key={f} cx={CX} cy={CY} r={DR * f} fill="none"
                stroke={axisStroke} strokeWidth="1"
                strokeDasharray={f === 1.0 ? '5 3' : '3 4'}
              />
            ))}

            {/* Axes */}
            <line x1="20" y1={CY} x2="310" y2={CY} stroke={axisStroke} strokeWidth="1" />
            <line x1={CX} y1="20" x2={CX} y2="310" stroke={axisStroke} strokeWidth="1" />

            {/* Axis labels */}
            <text x="295" y={CY - 6} fontSize="9.5" fill={textFill} fontFamily="IBM Plex Mono">+Re</text>
            <text x={CX + 5} y="32" fontSize="9.5" fill={textFill} fontFamily="IBM Plex Mono">+Im</text>
            <text x="6" y={CY + 14} fontSize="9" fill={textFill} fontFamily="IBM Plex Mono">−Re</text>
            <text x={CX - 20} y="318" fontSize="9" fill={textFill} fontFamily="IBM Plex Mono">−Im</text>

            {/* Magnitude label on circle */}
            <text x={CX + DR + 4} y={CY - 4} fontSize="9" fill={textFill} fontFamily="IBM Plex Mono" textAnchor="start">
              {vmag.toFixed(3)} kV
            </text>

            {/* Origin dot */}
            <circle cx={CX} cy={CY} r="3.5" fill={dark ? '#475569' : '#94a3b8'} />

            {/* Phasors */}
            {phasors.map(({ label, rad, tx, ty, ex, ey, disp, color }) => (
              <g key={label}>
                <line x1={CX} y1={CY} x2={ex} y2={ey}
                  stroke={color} strokeWidth="2.5" strokeLinecap="round" />
                <polygon points={arrowPoints(tx, ty, rad)} fill={color} />
                <text
                  x={tx + 18 * Math.cos(rad)}
                  y={ty - 18 * Math.sin(rad) + 4}
                  fontSize="13" fontWeight="700"
                  fontFamily="IBM Plex Mono"
                  fill={color} textAnchor="middle"
                >
                  {label}
                </text>
              </g>
            ))}
          </svg>
        </div>

        {/* Phasor value table + sum check */}
        <div className="flex-1 w-full space-y-3">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-500">Phasor values (balanced ABC)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="phasor-table">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500 dark:text-slate-500">Phasor</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500 dark:text-slate-500">Polar (kV ∠ °)</th>
                  <th className="py-1.5 px-2 text-left font-medium text-slate-500 dark:text-slate-500">Rectangular (kV)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {phasors.map(({ label, disp, re, im, color }) => (
                  <tr key={label}>
                    <td className="py-1.5 px-2 font-mono font-bold" style={{ color }}>{label}</td>
                    <td className="py-1.5 px-2 font-mono text-slate-700 dark:text-slate-300">
                      {vmag.toFixed(3)} ∠ {disp >= 0 ? '+' : ''}{disp.toFixed(1)}°
                    </td>
                    <td className="py-1.5 px-2 font-mono text-slate-700 dark:text-slate-300">
                      {re.toFixed(3)}{im >= 0 ? ' + j' : ' − j'}{Math.abs(im).toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3 space-y-1.5">
            <p className="text-xs text-slate-600 dark:text-slate-400">
              <span className="font-semibold text-teal-700 dark:text-teal-400">Phase sequence: </span>
              ABC (positive). Va leads Vb by 120°, Vb leads Vc by 120°.
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-500">
              <span className="font-semibold">Sum check: </span>
              Va + Vb + Vc = {sum.re.toFixed(2)} + j{sum.im.toFixed(2)} ≈ 0
              {' '}(balanced — neutral carries zero current)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhasorVisualizer;
